import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStoreState } from '~/store';
import { api } from '~/api';
import type { OrderData, TradeData } from '~/store/models/modelTypes';
import { useMemo } from 'react';
import { useFilters } from '~/components/filters/context';
import filterOrders from './filterMarket';
import { useProducts } from '~/hooks/useProducts';

/**
 * Hook to invalidate the market views only.
 * Use this when the market query params change (e.g. warehouse switch), not for
 * mutations — those should use useInvalidateOrderLists.
 */
export const useInvalidateMarketOrders = () => {
  const queryClient = useQueryClient();

  return () => {
    // Market + Marketplace tables
    queryClient.invalidateQueries({ queryKey: ['marketOrders'] });
    // Single order detail (order view / market row)
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  };
};

/**
 * Hook to invalidate every cached list that renders orders, triggering a refetch.
 * Use this after mutations that affect orders (creations, trades, counters, deletions, etc.)
 */
export const useInvalidateOrderLists = () => {
  const invalidateMarketOrders = useInvalidateMarketOrders();
  const queryClient = useQueryClient();

  return () => {
    invalidateMarketOrders();
    // Orders page table
    queryClient.invalidateQueries({ queryKey: ['ordersList'] });
    // Home page active listings
    queryClient.invalidateQueries({ queryKey: ['activeListings'] });
    // Solution picker order counts
    queryClient.invalidateQueries({
      queryKey: ['solutions', 'market-activity'],
    });
  };
};

interface MarketOrdersParams {
  solution: string;
  warehouse?: string;
}

interface MarketOrdersData {
  orders: OrderData[];
  participants: Record<string, string>;
  updatedDate: Date | null;
}

/**
 * Base hook for fetching market orders with caching and if-modified-since support.
 * Returns raw orders data without any filtering or sorting.
 * Used by Navbar (for counts), useFilterOptions (for origins), and Market page (via useFilteredMarketOrders).
 */
export const useMarketOrders = () => {
  const solution = useStoreState((state) => state.auth.solution);
  const { warehouse } = useFilters();
  const queryClient = useQueryClient();

  const queryKey = ['marketOrders', solution?.id, warehouse?.id];

  return useQuery<MarketOrdersData>({
    queryKey,
    queryFn: async () => {
      if (!solution?.id) {
        return { orders: [], participants: {}, updatedDate: null };
      }

      const params: MarketOrdersParams = { solution: solution.id };
      if (warehouse) {
        params.warehouse = warehouse.id;
      }

      const previousData = queryClient.getQueryData<MarketOrdersData>(queryKey);
      const updatedDate = previousData?.updatedDate;

      try {
        const result = await api.getDataIfModifiedSince(
          '/market-orders',
          params,
          updatedDate
        );

        if (!result) {
          return (
            previousData || { orders: [], participants: {}, updatedDate: null }
          );
        }

        const [status, response] = result;

        if (status === 200 && response) {
          const changed: OrderData[] = response.orders;
          const workingOrders: OrderData[] = [];
          const changedIds: string[] = [];

          changed.forEach((o) => {
            if (o.status === 'Working') {
              workingOrders.push(o);
            }
            changedIds.push(o.id);
          });

          // Merge with existing orders if this is an update
          if (updatedDate && previousData?.orders) {
            previousData.orders.forEach((order) => {
              if (!changedIds.includes(order.id)) {
                workingOrders.push(order);
              }
            });
          }

          return {
            orders: workingOrders,
            participants: {
              ...(previousData?.participants || {}),
              ...(response.participants || {}),
            },
            updatedDate: new Date(),
          };
        }

        // 304 Not Modified - return previous data with updated timestamp
        if (status === 304) {
          return (
            previousData || {
              orders: [],
              participants: {},
              updatedDate: new Date(),
            }
          );
        }

        // Unexpected status: degrade to cache if we have it, else surface it.
        if (previousData) return previousData;
        throw new Error(`Unexpected /market-orders response status: ${status}`);
      } catch (error) {
        console.error('Error fetching market orders:', error);
        // Degrade to cached data if we have it; otherwise surface the failure so
        // the query enters isError and the page can offer a retry, instead of
        // resolving to an empty marketplace that hides an initial backend failure.
        if (previousData) return previousData;
        throw error;
      }
    },
    enabled: !!solution?.id,
    refetchInterval: 30000, // 30 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is in background
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Only refetch on reconnect
    refetchOnMount: false, // Don't refetch when component remounts
    staleTime: 30000,
  });
};

interface PeriodAverage {
  avg: number;
  avg_5d: number | null;
  last_price: number;
  currency: string;
  trade_count: number;
  trade_count_5d: number;
  volume_weighted_avg: number;
  volume_weighted_avg_5d: number | null;
}

interface MarketTradesData {
  trades: TradeData[];
  periodAverages: Record<string, Record<string, PeriodAverage>>;
  updatedDate: Date | null;
}

/**
 * Hook for fetching market trades with caching and if-modified-since support.
 * Used by Marketplace table for displaying trade data.
 */
export const useMarketTrades = () => {
  const solution = useStoreState((state) => state.auth.solution);
  const queryClient = useQueryClient();

  const queryKey = ['marketTrades', solution?.id];

  return useQuery<MarketTradesData>({
    queryKey,
    queryFn: async () => {
      if (!solution?.id) {
        return { trades: [], periodAverages: {}, updatedDate: null };
      }

      const previousData = queryClient.getQueryData<MarketTradesData>(queryKey);
      const updatedDate = previousData?.updatedDate;

      try {
        const result = await api.getDataIfModifiedSince(
          '/market-trades',
          { solution: solution.id },
          updatedDate
        );

        if (!result) {
          return (
            previousData || {
              trades: [],
              periodAverages: {},
              updatedDate: null,
            }
          );
        }

        const [status, response] = result;

        if (status === 200 && response) {
          const changed: TradeData[] = response.trades;
          let trades: TradeData[] = [];
          const periodAverages = response.period_averages || {};

          // Merge with existing trades if this is an update
          if (updatedDate && previousData?.trades) {
            const changedIds = changed.map((t) => t.id);
            previousData.trades.forEach((trade) => {
              const idx = changedIds.indexOf(trade.id);
              if (idx === -1) {
                trades.push(trade);
              } else {
                trades.push(changed[idx]);
              }
            });
            // Add any new trades not in previous data
            changed.forEach((t) => {
              if (!trades.find((existing) => existing.id === t.id)) {
                trades.push(t);
              }
            });
          } else {
            trades = changed;
          }

          return {
            trades,
            periodAverages,
            updatedDate: new Date(),
          };
        }

        // 304 Not Modified - return previous data
        if (status === 304) {
          return (
            previousData || {
              trades: [],
              periodAverages: {},
              updatedDate: new Date(),
            }
          );
        }

        return (
          previousData || { trades: [], periodAverages: {}, updatedDate: null }
        );
      } catch (error) {
        console.error('Error fetching market trades:', error);
        return (
          previousData || { trades: [], periodAverages: {}, updatedDate: null }
        );
      }
    },
    enabled: !!solution?.id,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: false,
    staleTime: 30000,
  });
};

type SortableValue = string | number | Date | null | undefined;

function getValue(obj: any, path: string[]): SortableValue {
  let value = obj;
  for (const key of path) {
    value = value?.[key];
    if (value === undefined || value === null) return null;
  }
  return value;
}

function compareValues(
  a: SortableValue,
  b: SortableValue,
  ascending: boolean
): number {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  if (a < b) return ascending ? -1 : 1;
  if (a > b) return ascending ? 1 : -1;
  return 0;
}

function sortData(
  data: any[],
  sortFields: string[][],
  ascending: boolean
): any[] {
  return [...data].sort((a, b) => {
    for (const field of sortFields) {
      const aVal = getValue(a, field);
      const bVal = getValue(b, field);
      const comparison = compareValues(aVal, bVal, ascending);
      if (comparison !== 0) return comparison;
    }
    return 0;
  });
}

function getSorter(sortDirection: string, sortColumn: string) {
  const ascending = sortDirection === 'up';

  switch (sortColumn) {
    case 'etd-from':
      return (data: any[]) =>
        sortData(data, [['etd', 'from'], ['updated']], ascending);
    case 'etd-to':
      return (data: any[]) =>
        sortData(data, [['etd', 'to'], ['updated']], ascending);
    case 'origins':
      return (data: any[]) =>
        sortData(data, [['origins', '0', 'name'], ['updated']], ascending);
    case 'locations':
      return (data: any[]) =>
        sortData(data, [['locations', '0', 'name'], ['updated']], ascending);
    case 'product':
      return (data: any[]) =>
        sortData(data, [['product', 'name'], ['updated']], ascending);
    case 'created':
      return (data: any[]) => sortData(data, [['created']], ascending);
    case 'published':
      return (data: any[]) => sortData(data, [['published']], ascending);
    case 'ref-price':
      return (data: any[]) =>
        sortData(
          data,
          [['price', 'std', 'unit'], ['price', 'std', 'price'], ['updated']],
          ascending
        );
    case 'price':
      return (data: any[]) =>
        sortData(
          data,
          [['price', 'unit'], ['price', 'val'], ['updated']],
          ascending
        );
    case 'state':
      return (data: any[]) =>
        sortData(data, [['state'], ['updated']], ascending);
    default:
      return (data: any[]) => data;
  }
}

/**
 * Hook for the Market page that adds filtering and sorting to raw market orders.
 * Only this hook depends on filters and sorting state - keeps other consumers lightweight.
 * Used by the Market page Body component.
 */
export const useFilteredMarketOrders = (
  sortDirection: string,
  sortColumn: string
) => {
  const solution = useStoreState((state) => state.auth.solution);
  const hasRole = useStoreState((state) => state.auth.hasRole);
  const { filters } = useFilters();
  const queryClient = useQueryClient();

  const {
    data: products,
    isError: isProductsError,
    isFetching: isProductsFetching,
    refetch: refetchProducts,
  } = useProducts(solution?.id);

  // Compute tagProducts mapping from products
  const tagProducts = useMemo(() => {
    if (!products) return {};

    const mapping: Record<string, string[]> = {};
    products.forEach((product) => {
      const tags = product.tags || [];
      tags.forEach((tag: any) => {
        if (tag?.id) {
          if (!mapping[tag.id]) {
            mapping[tag.id] = [];
          }
          mapping[tag.id].push(product.id);
        }
      });
    });

    return mapping;
  }, [products]);

  const query = useMarketOrders();

  // Process the data: enrich → filter → sort
  const processedData = useMemo(() => {
    const orders = query.data?.orders;
    if (!orders || !products) return null;

    // 1. Enrich with state and key fields
    const enrichedOrders = orders.map((order) => {
      const product = products.find((p) => p.id === order.product.id);
      // Check if user has access to any variant product
      const orderVariants = (order as any).variants;
      const hasVariantAccess = Boolean(
        orderVariants?.some((v: any) =>
          products.some((p) => p.id === v.product?.id)
        )
      );
      const hasProductAccess = Boolean(product || hasVariantAccess);

      let state = 'viewonly';
      if (hasProductAccess) {
        state =
          hasRole('trader') || hasRole('manager')
            ? order.counteronly && !order.is_owner
              ? 'sfc-tradeable'
              : order.tradeable
                ? 'tradeable'
                : 'untradeable'
            : 'untradeable';
      }
      return {
        ...order,
        state,
        key: order.id,
        hasProductAccess,
      };
    });

    // 2. Filter
    const filteredOrders = filterOrders(enrichedOrders, filters, tagProducts);

    // 3. Sort
    const sorter = getSorter(sortDirection, sortColumn);
    return sorter(filteredOrders);
  }, [
    query.data?.orders,
    products,
    hasRole,
    filters,
    tagProducts,
    sortColumn,
    sortDirection,
  ]);

  // Surface a products error (and an initial market-orders error, see the queryFn)
  // so the page can offer a retry instead of spinning forever or showing a false
  // empty state. isFetching lets the page show a spinner during a retry (a v5
  // refetch keeps status === 'error' until it settles, so isError alone would
  // leave the error UI stuck on screen).
  //
  // refetch cancels first: a v5 refetch on a query with no cached data returns the
  // existing in-flight promise instead of starting a new request, so a hung initial
  // fetch would never actually be retried. cancelQueries drops fetchStatus to idle
  // so the refetch fires fresh requests. (The orphaned request is not aborted
  // without an AbortSignal wired through the API client — a deeper follow-up.)
  const refetch = async () => {
    await Promise.all([
      queryClient.cancelQueries({ queryKey: ['products', solution?.id] }),
      queryClient.cancelQueries({ queryKey: ['marketOrders', solution?.id] }),
    ]);
    return Promise.all([refetchProducts(), query.refetch()]);
  };

  return {
    ...query,
    data: processedData,
    isError: query.isError || isProductsError,
    isFetching: query.isFetching || isProductsFetching,
    refetch,
    participants: query.data?.participants || {},
  };
};
